/**
 * cs-plus-size-button.js
 * Web Component: resolves plus-size sibling products via Shopify product JSON API.
 * If a sibling exists, updates the link href. Otherwise keeps the fallback URL.
 *
 * Morph-safe: caches resolved handles so re-activation after Horizon morph
 * is instant (no network request). Document-level `variant:update` listener
 * re-activates elements whose --active class was stripped by morph.
 */
const resolvedCache = new Map();

class CsPlusSize extends HTMLElement {
  #controller = null;

  connectedCallback() {
    this.activate();
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  activate() {
    if (this.classList.contains('cs-plus-size--active')) return;

    const handle = this.dataset.plusSizeHandle;
    const link = this.querySelector('.cs-plus-size__link');

    if (!handle || !link) {
      this.classList.add('cs-plus-size--active');
      return;
    }

    // Cache hit — instant re-activation after morph
    if (resolvedCache.has(handle)) {
      link.href = resolvedCache.get(handle);
      this.classList.add('cs-plus-size--active');
      return;
    }

    this.resolveProduct(handle, link);
  }

  async resolveProduct(handle, link) {
    this.#controller?.abort();
    this.#controller = new AbortController();

    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}products/${handle}.json`, {
        signal: this.#controller.signal
      });

      if (response.ok) {
        const data = await response.json();
        const href = `${root}products/${data.product.handle}`;
        resolvedCache.set(handle, href);
        link.href = href;
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      // Network error — link stays as fallback
    }

    this.classList.add('cs-plus-size--active');
  }
}

customElements.define('cs-plus-size', CsPlusSize);

// Re-activate after Horizon morph strips the --active class
document.addEventListener('variant:update', function () {
  for (const el of document.querySelectorAll('cs-plus-size:not(.cs-plus-size--active)')) {
    el.activate();
  }
});
